import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { ArrowRight, Timer, ListChecks, BarChart3, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { CategoryIcon } from "@/components/Brand";
import { PackageCard } from "@/pages/Tryouts";

const Stat = ({ label, value, testid }) => (
  <div className="border-l-2 border-amber-400/70 pl-4">
    <div className="font-display text-3xl font-extrabold text-white" data-testid={testid}>{value ?? "–"}</div>
    <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">{label}</div>
  </div>
);

const FEATURES = [
  { icon: Timer, t: "Timer tervalidasi server", d: "Waktu ujian dihitung di server sehingga tidak dapat dimanipulasi dari browser." },
  { icon: ListChecks, t: "Navigasi CAT lengkap", d: "Nomor soal, tandai ragu-ragu, simpan otomatis, dan pemulihan sesi." },
  { icon: BarChart3, t: "Skor per materi", d: "Rincian skor setiap materi lengkap dengan Total Skor dan passing grade." },
  { icon: ShieldCheck, t: "Data aman", d: "Kunci jawaban tidak dikirim sebelum ujian selesai. Hasil hanya untuk pemiliknya." },
];

export default function Home() {
  const { settings } = useOutletContext();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [cats, setCats] = useState([]);
  const [pkgs, setPkgs] = useState([]);

  useEffect(() => {
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/packages").then((r) => setPkgs(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div>
      <section className="navy-hero grain relative overflow-hidden">
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:py-24">
          <div className="fade-up space-y-7 lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-amber-300">Platform Simulasi CAT & Tryout Online</span>
            <h1 className="text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-title">
              {settings?.site_name || "ADIKARA ACADEMY"}<br /><span className="gold-text">{settings?.tagline || "Langkah Cerdas, Raih Prestasi."}</span>
            </h1>
            <p className="max-w-xl text-base text-slate-300 md:text-lg">{settings?.about}</p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => nav("/tryout")} className="btn-gold flex items-center gap-2 rounded-lg px-7 py-3" data-testid="hero-start-btn">Mulai Tryout <ArrowRight className="h-4 w-4" /></button>
              <button onClick={() => nav("/kategori")} className="rounded-lg border border-white/25 px-7 py-3 font-semibold text-white transition-colors hover:bg-white/10" data-testid="hero-categories-btn">Lihat Kategori</button>
            </div>
            <div className="grid grid-cols-2 gap-6 pt-6 sm:grid-cols-4" data-testid="hero-stats">
              <Stat label="Kategori" value={stats?.categories} testid="stat-categories" />
              <Stat label="Paket aktif" value={stats?.packages} testid="stat-packages" />
              <Stat label="Soal" value={stats?.questions} testid="stat-questions" />
              <Stat label="Peserta" value={stats?.participants} testid="stat-participants" />
            </div>
          </div>
          <div className="relative hidden lg:col-span-5 lg:block">
            <div className="absolute -inset-3 rounded-2xl border border-amber-400/30" />
            <img src="/images/hero.jpg" alt="Peserta belajar" className="relative h-[480px] w-full rounded-2xl object-cover shadow-2xl" />
            <div className="absolute -bottom-6 -left-8 rounded-xl border border-amber-400/40 bg-[#0F213D]/95 p-4 text-white shadow-xl backdrop-blur">
              <div className="text-xs uppercase tracking-widest text-amber-300">Ujian selesai</div>
              <div className="font-display text-2xl font-extrabold">{stats?.finished ?? "–"}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-600">Kategori Ujian</p>
            <h2 className="mt-2 text-2xl font-bold text-[#0A192F] sm:text-3xl">Pilih jalur seleksi Anda</h2>
          </div>
          <Link to="/kategori" className="text-sm font-semibold text-[#0A192F] hover:text-amber-600" data-testid="home-all-categories">Semua kategori →</Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {cats.map((c, i) => (
            <Link key={c.id} to={`/tryout?kategori=${c.id}`} data-testid={`home-category-${c.slug}`} className="card-premium fade-up group p-6" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-[#0A192F] text-amber-400 transition-transform group-hover:scale-110"><CategoryIcon name={c.icon} /></div>
              <h3 className="font-display text-lg font-bold text-[#0A192F]">{c.name}</h3>
              <p className="mt-1 line-clamp-2 text-sm text-slate-500">{c.description}</p>
              <p className="mt-4 text-xs font-semibold text-amber-700">{c.subject_count} materi · {c.package_count} paket</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-600">Paket Tryout Terbaru</p>
          <h2 className="mb-10 mt-2 text-2xl font-bold text-[#0A192F] sm:text-3xl">Latihan dengan format CAT</h2>
          {pkgs.length ? (
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">{pkgs.map((p) => <PackageCard key={p.id} p={p} />)}</div>
          ) : <p className="text-sm text-slate-500">Belum ada paket tryout aktif.</p>}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <div className="grid gap-12 lg:grid-cols-2">
          <img src="/images/exam.jpg" alt="Simulasi ujian" className="h-80 w-full rounded-2xl object-cover shadow-lg lg:h-full" />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-600">Mengapa ADIKARA</p>
            <h2 className="mt-2 text-2xl font-bold text-[#0A192F] sm:text-3xl">Simulasi yang mendekati ujian sesungguhnya</h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              {FEATURES.map((f) => (
                <div key={f.t} className="space-y-2">
                  <f.icon className="h-6 w-6 text-amber-500" />
                  <h3 className="font-bold text-[#0A192F]">{f.t}</h3>
                  <p className="text-sm text-slate-500">{f.d}</p>
                </div>
              ))}
            </div>
            <button onClick={() => nav("/daftar")} className="btn-gold mt-10 rounded-lg px-7 py-3" data-testid="home-cta-register">Daftar Sekarang</button>
          </div>
        </div>
      </section>
    </div>
  );
}
